// A specialist's next actions as a table: one row per action, opened for its context, why
// it blocks, what to do next and why its date. Rows are ordered by what matters most and
// coloured by the dates they work to. Draft email starts a conversation with the owning
// specialist at once; Discuss opens it for the operator's own question. Both show in the
// agent pane beside the page and are listed with the project's conversations.
// See docs/Y2026-Q4/design-structured-next-actions-and-dates.md 7.2.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { areaLabel } from "../api/hooks/buildingReadiness";
import type {
  ActionDraft,
  ActionIntent,
  DateMoveSubject,
  NextAction,
  TrackedDate,
} from "../api/hooks/useProjectAgentStatus";
import { daysSince } from "../api/hooks/useProjectAgentStatus";
import { useAgentPane } from "../state/useAgentPane";
import { formatDay, formatShortDay } from "../utils/attributeFormat";
import {
  type ActionSort,
  type ActionSource,
  actionKind,
  closedByLabel,
  compareActions,
  hardDeadline,
  type DateTone,
  deadlineTone,
  type ManagerRanking,
  dueHint,
  openPicks,
  dueTone,
  onWhom,
  type ProjectItems,
  projectItems,
  recipient,
  ownerName,
  type PartTone,
  statusLabel,
  statusParts,
  typeLine,
  TYPE_LABEL,
  STATUS_LABEL,
  waitUntil,
  workedCheck,
  worstTone,
} from "../utils/nextActions";
import {
  type ActionChange,
  actionChange,
  agentOverride,
  changeTally,
} from "../utils/actionChanges";
import type { BulkRow } from "../utils/bulkActions";
import { reviewAttrs } from "../utils/reviewState";
import { agentDisplayLabel } from "../utils/runParticipants";
import { ActionControls } from "./ActionControls";
import { AddToCalendarControl } from "./AddToCalendar";
import { BulkActions } from "./BulkActions";
import controlStyles from "./ActionControls.module.css";
import { ActionDetails, type Row } from "./ActionDetails";
import { PeekButton } from "./PeekButton";
import { InfoToggle } from "./InfoToggle";
import { isTextSelectionClick } from "./selectableButton";
import { MultiSelectFilter } from "./MultiSelectFilter";
import {
  RunNamesProvider,
  useRunNameMap,
  useRunNames,
} from "../api/hooks/useRunNames";
import styles from "./NextActionTable.module.css";
import { useHashOpen } from "../utils/useScrollToHash";
import { Chevron } from "./Chevron";
import { useAgentSlot } from "../api/agentSlot";
import { useDismissActions } from "../api/hooks/useActionChanges";
import { projectSpecialistPath } from "../routes/learningAgentPaths";

const RECENTLY_CLOSED_DAYS = 14;

const everything = () => true;

/** The URL values of ``show`` that narrow every action table on the page to its overdue
 * (red) or upcoming (yellow) actions, or both (``overdue,upcoming``), so a link (the
 * dashboard's chips) can land on them. */
export const SHOW_OVERDUE = "overdue";
export const SHOW_UPCOMING = "upcoming";
export type DueShow = typeof SHOW_OVERDUE | typeof SHOW_UPCOMING;

const SHOW_TONE: Record<DueShow, DateTone> = {
  [SHOW_OVERDUE]: "missed",
  [SHOW_UPCOMING]: "near",
};

const DUE_SHOWS: readonly DueShow[] = [SHOW_OVERDUE, SHOW_UPCOMING];

/** The ``show`` values in the URL, in chip order; anything else is ignored. */
const asDueShows = (value: string | null): DueShow[] => {
  const named = (value ?? "").split(",");
  return DUE_SHOWS.filter((show) => named.includes(show));
};

const TONE_CLASS: Record<DateTone, string> = {
  "": "",
  near: styles.near,
  missed: styles.missed,
};

/** Whose actions a table lists: one specialist's; every specialist's on the project, led
 * by the Manager's ranking and with nothing closed left out; or one person's across
 * projects, led by each project's Manager's ranking, its closed ones behind a toggle
 * (docs/Y2026-Q4/design-my-actions.md). */
export type ActionScope =
  | { kind: "agent"; agent: string }
  | { kind: "all" }
  | {
      kind: "person";
      /** The InRange owner whose actions are listed, as actions store it. */
      email: string;
      name: string;
      /** Whether that person is the one looking: "On me" rather than "On <name>". */
      viewer: boolean;
    };

/** ``sources`` as last time while every source is the same project, data and ranking,
 * so a caller that builds the list inline does not rebuild the rows on every render. */
const useStableSources = (sources: ActionSource[]): ActionSource[] => {
  const kept = useRef(sources);
  const last = kept.current;
  const same =
    last.length === sources.length &&
    last.every(
      (was, i) =>
        was.projectId === sources[i].projectId &&
        was.projectName === sources[i].projectName &&
        was.data === sources[i].data &&
        was.manager === sources[i].manager,
    );
  if (!same) kept.current = sources;
  return kept.current;
};

/** A row with the project it comes from. */
interface TableRow extends Row {
  source: ActionSource;
  items: ProjectItems;
  /** Its project's Manager's open picks, which the priority column shows. */
  picks: ManagerRanking | undefined;
  /** What has moved on it since the page opened; null when nothing has. */
  change: ActionChange | null;
  /** Open when the page opened and no longer on the list: kept, greyed, as it was then,
   * so it does not vanish from under the operator. Nothing can be done to it here. */
  gone: boolean;
}

/** Whether an agent is taking in a change on the row's project that may move the row:
 * a review in flight on its specialist's lists, or on the project as a whole. */
const updating = (row: TableRow): boolean => {
  const applying = row.source.data.applying_review;
  if (!applying || row.action.closed || row.gone) return false;
  // A row saved before the specialists were sent names none: any may move.
  if (!applying.agents || applying.whole_project) return true;
  return applying.agents.includes(row.agent);
};

/** The due date as a chip coloured by how near it is; "-" when closed or undated. A
 * waiting action reads "after" the latest date of what it waits on, uncoloured: its own
 * date is not in force until then. The external deadline it serves shows under it, so the
 * date the work protects is never hidden behind the row, and so does an agent's open
 * proposal to move it. */
const DueChip = ({
  action,
  items,
  deadline,
  today,
  dateMove,
}: {
  action: NextAction;
  items: ProjectItems;
  deadline: TrackedDate | undefined;
  today: string;
  dateMove: DateMoveSubject | undefined;
}) => {
  const { date, target, title } = dueHint(action);
  const waiting = !action.closed && action.status?.status === "waiting";
  const until = waiting ? waitUntil(action, items) : "";
  const served =
    !action.closed &&
    deadline?.date &&
    !deadline.closed &&
    deadline.date !== date ? (
      <span className={styles.deadlineNote} title={deadline.what}>
        {deadline.hardness === "hard" ? "deadline" : "target"}{" "}
        {formatDay(deadline.date)}
      </span>
    ) : null;
  if (waiting)
    return (
      <>
        {until ? (
          <span
            className={styles.chip}
            title={
              "Goes ahead once what it waits on is done" +
              (date ? `; its own date was ${formatDay(date)}` : "")
            }
          >
            after {formatDay(until)}
          </span>
        ) : (
          <span className={styles.dash}>-</span>
        )}
        {served}
      </>
    );
  return action.closed || !date ? (
    <>
      <span className={styles.dash}>-</span>
      {served}
    </>
  ) : (
    <>
      <span
        className={`${styles.chip} ${TONE_CLASS[dueTone(action, today)]}`}
        title={title}
      >
        {formatDay(date)}
        {target && "*"}
      </span>
      {dateMove && (
        <span className={controlStyles.proposedChip} title={dateMove.reason}>
          → {formatShortDay(dateMove.proposed)} proposed
        </span>
      )}
      {served}
    </>
  );
};

/** The name the On column shows: the person outside when it is on them, else our owner;
 * "" once closed. A wait names its person; one that names nobody reads "Them". */
const onName = (action: NextAction, owner: string): string => {
  if (action.closed) return "";
  if (onWhom(action).side !== "them") return owner;
  return action.status?.status === "waiting"
    ? action.waits_for?.who?.name || "Them"
    : action.external_owner?.name || "Them";
};

/** Who has to act now: our owner's name when it is on us - in red when it is the chase
 * of a late reply - or the person outside. */
const OnChip = ({
  action,
  owner,
}: {
  action: NextAction;
  /** The InRange owner's name. */
  owner: string;
}) => {
  const on = onWhom(action);
  if (action.closed) return <span className={styles.dash}>-</span>;
  if (on.side === "them") {
    const waiting = action.status?.status === "waiting";
    const name = onName(action, owner);
    return (
      <span
        className={styles.theirChip}
        title={waiting && name === "Them" ? action.waits_for?.event : on.party}
      >
        {name}
      </span>
    );
  }
  return owner ? (
    <span
      className={`${styles.ownerChip} ${on.overdue ? styles.missed : ""}`}
      title={
        on.overdue
          ? `${recipient(action) || "Their"} reply is overdue: the chase is ours`
          : undefined
      }
    >
      {owner}
    </span>
  ) : (
    <span className={styles.dash}>-</span>
  );
};

const PART_CHIP: Record<PartTone, string> = {
  "": styles.chip,
  near: `${styles.chip} ${styles.near}`,
  missed: `${styles.chip} ${styles.missed}`,
  closed: styles.chipClosed,
  blocking: styles.chipBlocking,
};

const StatusChips = ({
  action,
  deadline,
  today,
  dateMove,
}: {
  action: NextAction;
  deadline: TrackedDate | undefined;
  today: string;
  dateMove: DateMoveSubject | undefined;
}) => (
  <span className={styles.chips}>
    {statusParts(action, deadline, today).map((part) => (
      <span key={part.text} className={PART_CHIP[part.tone]}>
        {part.text}
      </span>
    ))}
    {dateMove && !action.closed && (
      <span className={controlStyles.proposedChip}>Date move to approve</span>
    )}
  </span>
);

const placeWord = (place: number) => (place === 1 ? "first" : `#${place}`);

/** The row's place: its specialist's own on a specialist's table, the Manager's across the
 * project on All actions (where a row added since the Manager last ranked reads "-"). The number opens
 * why it ranks there; nothing shows once the action is closed. */
const PriorityCell = ({
  action,
  agentLabel,
  manager,
}: {
  action: NextAction;
  agentLabel: string;
  /** The Manager's ranking on All actions; undefined on a specialist's own table. */
  manager: ManagerRanking | undefined;
}) => {
  if (action.closed) return null;
  const own =
    action.priority !== null && action.priority !== undefined
      ? { place: action.priority, why: action.priority_reason }
      : null;
  const pick = manager?.get(action.action_id);
  const place = manager ? pick?.place : own?.place;
  const ownWhy = own && (
    <span className={styles.rankWhy}>
      <strong>
        {manager
          ? `${agentLabel}'s own #${own.place}`
          : `Why it's ${placeWord(own.place)}`}
      </strong>
      {own.why || "No reason recorded for this ranking."}
    </span>
  );
  const note = !manager ? (
    own?.why && ownWhy
  ) : (
    (pick || own) && (
      <>
        <span className={styles.rankWhy}>
          <strong>
            {pick
              ? `Manager's #${pick.place} across the project`
              : manager.size > 0
                ? "Not placed by the Manager"
                : "No ranking from the Manager"}
          </strong>
          {pick?.why}
        </span>
        {ownWhy}
      </>
    )
  );
  // The column's only label is the header, so each number says what it is.
  const chip = (
    <>
      <span className={styles.srOnly}>{place ? "Priority " : "Not ranked"}</span>
      {place ? (
        <span className={styles.rank}>{place}</span>
      ) : (
        <span className={styles.dash} aria-hidden="true">
          -
        </span>
      )}
    </>
  );
  if (!note) return chip;
  return (
    // The row opens on a click; the number only opens its reason.
    <span onClick={(e) => e.stopPropagation()}>
      <InfoToggle label="Why it ranks there" trigger={chip}>
        {note}
      </InfoToggle>
    </span>
  );
};

/** Why nothing works towards a deadline: "No action needed" with the reason on hover,
 * or, with no reason, a red "Nobody on it", as Tracked dates says. */
export const CoverChip = ({ reason }: { reason: string }) =>
  reason ? (
    <span className={styles.chip} title={reason}>
      No action needed
    </span>
  ) : (
    <span className={`${styles.chip} ${styles.missed}`}>Nobody on it</span>
  );

/** Who dismissed an action until the next run, and when, as its chip's hover says it. */
const dismissedTitle = (row: TableRow, by: string, at: string): string =>
  `Dismissed by ${row.source.data.team[by.toLowerCase()] ?? (by || "an operator")} on ${formatDay(at.slice(0, 10))}. ` +
  `Back once the next daily run has worked ${row.source.projectName || "this project"}.`;

/** Dismiss an action until the next run, or bring it back now; a dismissed row folds
 * away (``onDismissed``), as a push or resolve does. */
const DismissButton = ({
  row,
  dismissed,
  onDismissed,
}: {
  row: TableRow;
  dismissed: boolean;
  onDismissed: () => void;
}) => {
  const dismiss = useDismissActions();
  return (
    <>
      <button
        type="button"
        className={styles.button}
        disabled={dismiss.isLoading}
        title={
          dismissed
            ? undefined
            : "Dealt with for now: shows greyed, out of the counts, until the next daily run. The action stays open."
        }
        onClick={() =>
          dismiss.mutate(
            {
              project_id: row.source.projectId,
              action_ids: [row.action.action_id],
              dismissed: !dismissed,
            },
            {
              onSuccess: () => {
                if (!dismissed) onDismissed();
              },
            },
          )
        }
      >
        {dismissed ? "Undo dismiss" : "Dismiss until next run"}
      </button>
      {dismiss.error && <p className={controlStyles.error}>{dismiss.error.message}</p>}
    </>
  );
};

const ActionRow = ({
  row,
  showAgent,
  showProject,
  columns,
  open,
  onToggle,
  onClose,
  anchored,
  pick,
}: {
  row: TableRow;
  /** Adds the column naming the specialist, for a table that mixes them. */
  showAgent: boolean;
  /** Adds the column naming the project, for a table across projects. */
  showProject: boolean;
  columns: number;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  anchored: boolean;
  /** The row's tick box, on a table that takes bulk actions; null on one that does not. */
  pick: { checked: boolean; onChange: (on: boolean) => void } | null;
}) => {
  const { action, deadline, items, source } = row;
  const { projectId } = source;
  const { team } = source.data;
  const today = source.data.lists_today;
  const manager = row.picks;
  const dateMove = source.data.date_moves?.[action.action_id];
  const draft = source.data.action_drafts[action.action_id];
  const dismissal = !action.closed ? source.data.action_dismissals?.[action.action_id] : undefined;
  // The agent's run moved the date after the operator did: their change did not stand.
  const override = !row.gone ? agentOverride(action) : null;
  // The conversation each button already started, which it reopens.
  const conversations = source.data.action_conversations?.[action.action_id] ?? {};
  const pane = useAgentPane();
  const slot = useAgentSlot();
  // The pane renders the details outside this table, so they take its run names along.
  const runNames = useRunNameMap();
  // Started from the pane this session, ahead of the server's map catching up.
  const earlier = (intent: ActionIntent) =>
    pane.startedRun(projectId, slot, action.action_id, intent) ??
    conversations[intent] ??
    null;
  const status = action.status;
  // Their reply is overdue: the email to draft is the chase.
  const overdue = status?.status === "to_chase";
  const answering =
    status?.status === "reply_received" || status?.status === "answer_overdue";
  const detailsWith = (move: DateMoveSubject | undefined) => (
    <ActionDetails
      row={row}
      items={items}
      agent={row.agent}
      projectId={projectId}
      today={today}
      team={team}
      dateMove={move}
    />
  );
  const details = detailsWith(dateMove);
  const summary: ReactNode = (
    <>
      <strong>{action.title}</strong>
      <span className={styles.typeLine}>
        {row.area && `${row.area} · `}
        {typeLine(action)}
        {action.points.length > 0 &&
          ` · ${action.points.filter((p) => p.state === "open").length} open of ${action.points.length}`}
      </span>
    </>
  );
  const openInPane = (intent: ActionIntent, email: ActionDraft | null = null) =>
    pane.open({
      projectId,
      config: slot,
      agent: row.agent,
      actionId: action.action_id,
      intent,
      title: action.title,
      kind: actionKind(action.type),
      context: (
        <RunNamesProvider value={runNames}>
          <p className={styles.contextHead}>{summary}</p>
          {/* The pane keeps this as a snapshot, so a date move's answer would go stale. */}
          {detailsWith(undefined)}
        </RunNamesProvider>
      ),
      runId: earlier(intent),
      email,
    });

  return (
    <tbody
      // A comment selected in the row is on this action, for its specialist. A table
      // across projects (My actions) also names the row's project, as there is no page
      // project to fall back on; a one-project page keeps its own.
      {...reviewAttrs("specialist_details", `${row.agentLabel} > ${action.title}`.slice(0, 200), {
        agent: row.agent,
        itemId: action.action_id,
        doc: showProject ? `project:${projectId}` : undefined,
      })}
      id={anchored && !row.gone ? action.action_id : undefined}
      className={`${styles.item} ${TONE_CLASS[row.tone]} ${action.closed || row.gone ? styles.closed : ""} ${open ? styles.open : ""} ${row.change ? styles.changed : ""} ${dismissal && !open ? styles.dismissed : ""}`}
    >
      <tr
        className={styles.row}
        onClick={(e) => {
          if (!row.gone && !isTextSelectionClick(e)) onToggle();
        }}
      >
        {pick && (
          <td className={styles.pickCell} onClick={(e) => e.stopPropagation()}>
            {!action.closed && (
              <input
                type="checkbox"
                aria-label={`Select ${action.title}`}
                checked={pick.checked}
                onChange={(e) => pick.onChange(e.target.checked)}
              />
            )}
          </td>
        )}
        <td className={styles.rankCell}>
          {!row.gone && (
            <PriorityCell
              action={action}
              agentLabel={row.agentLabel}
              manager={manager}
            />
          )}
        </td>
        <td className={styles.titleCell}>
          <button type="button" className={styles.toggle} aria-expanded={open}>
            <span className={styles.caret} aria-hidden="true">
              <Chevron tree open={open} />
            </span>
            <span className={styles.titleText}>{summary}</span>
          </button>
        </td>
        {showProject && (
          <td>{source.projectName}</td>
        )}
        {showAgent && (
          <td>
            <PeekButton projectId={projectId} agent={row.agent} look="chip">
              {row.agentLabel}
            </PeekButton>
          </td>
        )}
        <td>
          <DueChip
            action={action}
            items={items}
            deadline={deadline}
            today={today}
            dateMove={dateMove}
          />
        </td>
        <td>
          <OnChip action={action} owner={row.owner} />
        </td>
        <td>
          {dismissal && !row.gone && (
            <span
              className={styles.dismissedChip}
              title={dismissedTitle(row, dismissal.by, dismissal.at)}
            >
              Dismissed until next run
            </span>
          )}
          {!row.gone && !dismissal && (
            <StatusChips
              action={action}
              deadline={deadline}
              today={today}
              dateMove={dateMove}
            />
          )}
          {override && (
            <span
              className={`${styles.chip} ${styles.near}`}
              title={
                `Your date ${override.from ? formatDay(override.from) : "(none)"}, moved to ` +
                `${override.to ? formatDay(override.to) : "no date"}` +
                (override.note ? `: ${override.note}` : "")
              }
            >
              {agentDisplayLabel(override.agent)} changed your date
            </span>
          )}
          {updating(row) && (
            <span className={styles.updating} title="An agent is taking in a change on these actions">
              Agent updating…
            </span>
          )}
          {row.change && (
            <span className={styles.changeNote} title="Since you opened this page">
              {row.change.notes.join(" · ")}
            </span>
          )}
          {!action.closed && !row.gone && !workedCheck(items, action) && (
            <span
              className={`${styles.chip} ${styles.missed}`}
              title="Works towards no open, unmet Ready to build item"
            >
              No checklist item
            </span>
          )}
        </td>
      </tr>
      {open && !row.gone && (
        <tr className={styles.detailRow}>
          <td colSpan={columns}>
            {details}
            {!action.closed && (
              <div className={styles.buttons}>
                {action.type === "contact" && draft && (
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => {
                      // The conversation that drafted it shows the email and takes changes.
                      const intent = earlier("draft_reminder")
                        ? "draft_reminder"
                        : "draft_email";
                      // A scheduled pass's draft shows on its own, to discuss.
                      if (earlier(intent)) openInPane(intent);
                      else openInPane("discuss", draft);
                    }}
                  >
                    Open email
                  </button>
                )}
                {action.type === "contact" &&
                  status?.status !== "waiting" &&
                  (!draft || overdue) && (
                    <button
                      type="button"
                      className={`${styles.button} ${styles.primary}`}
                      onClick={() =>
                        openInPane(overdue ? "draft_reminder" : "draft_email")
                      }
                    >
                      {overdue
                        ? "Draft reminder"
                        : answering
                          ? "Draft reply"
                          : "Draft email"}{" "}
                      to {action.external_owner?.name || recipient(action)}
                    </button>
                  )}
                <ActionControls
                  action={action}
                  agent={row.agent}
                  projectId={projectId}
                  version={row.version}
                  today={today}
                  pushable={!dateMove}
                  onChanged={onClose}
                />
                <AddToCalendarControl
                  actionId={action.action_id}
                  title={action.title}
                  due={action.due ?? ""}
                  deadline={deadline && !deadline.closed ? deadline.date : ""}
                  today={today}
                  description={[
                    `From ${row.agentLabel} on ${source.projectName}.`,
                    `${window.location.origin}${projectSpecialistPath(projectId, row.agent)}#${action.action_id}`,
                  ].join("\n\n")}
                />
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => openInPane("discuss")}
                >
                  Discuss with agent
                </button>
                <DismissButton row={row} dismissed={!!dismissal} onDismissed={onClose} />
              </div>
            )}
          </td>
        </tr>
      )}
    </tbody>
  );
};

interface Filters {
  project: string[];
  agent: string[];
  on: string[];
  owner: string[];
  status: string[];
  type: string[];
}

/** "me" is "us" narrowed to the actions whose InRange owner is the viewer. */
type Side = "me" | "us" | "them";

const NO_FILTERS: Filters = {
  project: [],
  agent: [],
  on: [],
  owner: [],
  status: [],
  type: [],
};

const filterValue: Record<keyof Filters, (row: TableRow) => string> = {
  project: (row) => row.source.projectName,
  agent: (row) => row.agentLabel,
  on: (row) =>
    row.action.closed ? "Closed" : onName(row.action, row.owner) || "Nobody",
  owner: (row) => row.owner,
  status: (row) => statusLabel(row.action),
  type: (row) => TYPE_LABEL[row.action.type],
};

const FILTER_NOUN: Record<keyof Filters, { one: string; many: string }> = {
  project: { one: "project", many: "projects" },
  agent: { one: "agent", many: "agents" },
  on: { one: "person it's on", many: "people it's on" },
  owner: { one: "InRange owner", many: "InRange owners" },
  status: { one: "status", many: "statuses" },
  type: { one: "type", many: "types" },
};

// Which agent and who it is on matter only where a table mixes specialists; the owner
// is fixed on one person's.
const FILTERS_BY_SCOPE: Record<ActionScope["kind"], (keyof Filters)[]> = {
  agent: ["owner", "status", "type"],
  all: ["agent", "on", "owner", "status", "type"],
  person: ["project", "agent", "on", "status", "type"],
};

const STATUS_ORDER = Object.values(STATUS_LABEL);

/** What a table's controls are set to. Kept in the tab's session storage, so a reload keeps
 * them and closing the tab forgets them (Noaa, 29 Oct 2026), as the dashboard's filters. */
interface TableView {
  filters: Filters;
  query: string;
  /** "On me" / "On us" / "On them": none pressed shows both sides. */
  sides: Side[];
  blockingOnly: boolean;
  movesOnly: boolean;
  /** One person's closed actions show only on request; Clear filters leaves this alone. */
  showClosed: boolean;
  sort: ActionSort;
}

const SIDES: readonly Side[] = ["me", "us", "them"];
const SORTS: readonly ActionSort[] = ["priority", "due", "deadline"];

/** One person's list opens on what is on them, soonest due first (Noaa, 29 Oct 2026). */
const defaultView = (person: boolean): TableView => ({
  filters: NO_FILTERS,
  query: "",
  sides: person ? ["us"] : [],
  blockingOnly: false,
  movesOnly: false,
  showClosed: false,
  sort: person ? "due" : "priority",
});

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

/** The view stored under ``key``, each part checked; anything missing or malformed reads
 * as the default. */
const readView = (key: string | null, person: boolean): TableView => {
  const fallback = defaultView(person);
  if (!key) return fallback;
  let stored: unknown = null;
  try {
    stored = JSON.parse(window.sessionStorage.getItem(key) ?? "null");
  } catch {
    return fallback;
  }
  if (!stored || typeof stored !== "object") return fallback;
  const s = stored as Partial<Record<keyof TableView, unknown>>;
  const f = (s.filters && typeof s.filters === "object" ? s.filters : {}) as Partial<
    Record<keyof Filters, unknown>
  >;
  const flag = (value: unknown, otherwise: boolean) =>
    typeof value === "boolean" ? value : otherwise;
  return {
    filters: {
      project: strings(f.project),
      agent: strings(f.agent),
      on: strings(f.on),
      owner: strings(f.owner),
      status: strings(f.status),
      type: strings(f.type),
    },
    query: typeof s.query === "string" ? s.query : fallback.query,
    sides: Array.isArray(s.sides)
      ? SIDES.filter((side) => (s.sides as unknown[]).includes(side))
      : fallback.sides,
    blockingOnly: flag(s.blockingOnly, fallback.blockingOnly),
    movesOnly: flag(s.movesOnly, fallback.movesOnly),
    showClosed: flag(s.showClosed, fallback.showClosed),
    sort: SORTS.find((sort) => sort === s.sort) ?? fallback.sort,
  };
};

/** A table's view, remembered under ``key`` for the tab's session; null keeps it for this
 * visit only. A new key (another project's tab in the same component) reads its own. */
const useTableView = (key: string | null, person: boolean) => {
  const [held, setHeld] = useState(() => ({ key, view: readView(key, person) }));
  const view = held.key === key ? held.view : readView(key, person);
  useEffect(() => {
    if (!held.key) return;
    try {
      window.sessionStorage.setItem(held.key, JSON.stringify(held.view));
    } catch {
      // Not remembered; the view still holds for this visit.
    }
  }, [held]);
  const update = (change: (was: TableView) => Partial<TableView>) =>
    setHeld((was) => {
      const current = was.key === key ? was.view : readView(key, person);
      return { key, view: { ...current, ...change(current) } };
    });
  return [view, update] as const;
};

/** Everything a row says, for the search box. */
const searchText = (row: TableRow): string =>
  [
    row.action.title,
    row.source.projectName,
    row.action.summary,
    row.action.context,
    row.action.call_to_action,
    row.agentLabel,
    row.owner,
    onName(row.action, row.owner),
    recipient(row.action),
    statusLabel(row.action),
  ]
    .join("\n")
    .toLowerCase();

/** A specialist's actions as a table, every specialist's on a project, or one person's
 * across projects (``scope``), narrowed by ``include`` (Building readiness shows each
 * area's inside the area). ``controls`` adds the filters and sort; ``showArea`` names
 * each action's area, for a table that mixes them. */
export const ActionTable = ({
  scope,
  sources,
  include = everything,
  empty,
  controls,
  showArea,
  anchored,
}: {
  scope: ActionScope;
  sources: ActionSource[];
  include?: (action: NextAction) => boolean;
  empty: string;
  controls: boolean;
  showArea: boolean;
  /** Whether this is the page's own table: each row carries its action's id for a link to
   * land on, and the page's Overdue / Upcoming setting in the URL narrows it. False for a
   * second copy (a checklist item's, the agent pane's), so a link lands on the one table
   * and the page's setting leaves the copy whole. */
  anchored: boolean;
}) => {
  const person = scope.kind === "person" ? scope : null;
  const { user } = useKindeAuth();
  const viewer = (user?.email ?? "").toLowerCase();
  const { openIds, toggle, close } = useHashOpen();
  const { pathname } = useLocation();
  // Only a table with controls has a view worth keeping; each page and scope keeps its own.
  const viewKey = controls
    ? `action-table:${pathname}:${scope.kind === "agent" ? scope.agent : (person?.email ?? "all")}`
    : null;
  const [view, updateView] = useTableView(viewKey, !!person);
  const { filters, query, sides, blockingOnly, movesOnly, showClosed, sort } = view;
  // The open actions ticked for a bulk action, on a table that takes them.
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  // Each row as the page opened with it, by action id: what the change notes measure from.
  // Kept per list (``viewKey``): moving to another specialist's tab is another list.
  const opened = useRef<{ key: string | null; rows: Map<string, TableRow> } | null>(null);
  // Only the rows that have changed since then, from the tally's link.
  const [changedOnly, setChangedOnly] = useState(false);
  // In the URL rather than held here, so a link can set it and every table on the page
  // (Building readiness has one per area) follows the one setting.
  const [searchParams, setSearchParams] = useSearchParams();
  // Overdue and Upcoming are a multi-select: both pressed shows either (Noaa, 28 Oct 2026).
  const dueShown = anchored ? asDueShows(searchParams.get("show")) : [];
  const setDueShown = (shows: DueShow[]) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (shows.length > 0) next.set("show", shows.join(","));
        else next.delete("show");
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  const toggleDue = (show: DueShow) =>
    setDueShown(
      DUE_SHOWS.filter((s) => (s === show ? !dueShown.includes(s) : dueShown.includes(s))),
    );

  const showAgent = scope.kind !== "agent";
  const showProject = scope.kind === "person";
  const oneAgent = scope.kind === "agent" ? scope.agent : null;
  const owner = person?.email.toLowerCase() ?? null;
  const stableSources = useStableSources(sources);
  const prepared = useMemo(
    () =>
      stableSources.map((source) => {
        const items = projectItems(source.data);
        return {
          source,
          items,
          picks: source.manager ? openPicks(source.manager, items) : undefined,
        };
      }),
    [stableSources],
  );
  const rows: TableRow[] = useMemo(() => {
    // Action ids are unique across projects, so every project's picks share one map.
    const picks: ManagerRanking = new Map(
      prepared.flatMap(({ picks }) => [...(picks ?? [])]),
    );
    const listed = prepared
      .flatMap(({ source, items, picks }) =>
        source.data.agents
          .filter((entry) => oneAgent === null || entry.agent === oneAgent)
          .flatMap((entry) =>
            (entry.lists?.actions ?? []).map((action) => ({
              source,
              items,
              picks,
              entry,
              action,
            })),
          ),
      )
      .filter(
        ({ action }) =>
          include(action) &&
          (owner === null || action.internal_owner.toLowerCase() === owner) &&
          // Every closed action where the table lists every agent's on a project; the
          // recent ones elsewhere.
          (scope.kind === "all" ||
            !action.closed ||
            (daysSince(action.closed.at.slice(0, 10)) ?? 0) <=
              RECENTLY_CLOSED_DAYS),
      );
    // Action ids are unique across projects, so one map holds every row's deadline.
    const deadlines = new Map(
      listed.map(({ action, items }) => [
        action.action_id,
        hardDeadline(action.serves ? items.dates.get(action.serves)?.date : undefined),
      ]),
    );
    const order = compareActions(sort, picks, {
      ownRanking: scope.kind !== "person",
      deadlineOf: (a) => deadlines.get(a.action_id) ?? "",
    });
    const built: TableRow[] = listed.map(({ source, items, picks, entry, action }) => {
      const today = source.data.lists_today;
      const deadline = action.serves
        ? items.dates.get(action.serves)?.date
        : undefined;
      return {
        source,
        items,
        picks,
        action,
        agent: entry.agent,
        agentLabel: entry.label,
        version: entry.lists?.version ?? 0,
        owner: ownerName(source.data.team, action),
        closedBy: action.closed?.by
          ? closedByLabel(
              action.closed.by,
              source.data.team,
              agentDisplayLabel,
            )
          : "",
        deadline,
        tone: worstTone(
          dueTone(action, today),
          deadlineTone(action, deadline, today),
        ),
        area: showArea ? areaLabel(action.area) : "",
        change: null,
        gone: false,
      };
    });
    // Only a full list says what has moved; a copy elsewhere (a checklist item's, the agent
    // pane's) shows the rows as they are.
    if (!controls) return built.sort((a, b) => order(a.action, b.action));
    // The list as the page opened with it, which every change is measured against.
    if (opened.current === null || opened.current.key !== viewKey)
      opened.current = {
        key: viewKey,
        rows: new Map(built.map((row) => [row.action.action_id, row])),
      };
    const was = opened.current.rows;
    const here = new Set(built.map((row) => row.action.action_id));
    const ghosts: TableRow[] = [...was.values()]
      .filter((row) => !here.has(row.action.action_id) && !row.action.closed)
      .map((row) => ({ ...row, tone: "" as DateTone, gone: true }));
    const marked = [...built, ...ghosts].map((row) => ({
      ...row,
      change: actionChange(
        was.get(row.action.action_id)?.action,
        row.gone ? undefined : row.action,
      ),
    }));
    // A row closed or gone since the page opened keeps the place it had rather than
    // dropping to the closed ones at the end, so it does not jump away from the operator.
    const sortAs = (row: TableRow): NextAction =>
      (row.gone || row.change?.kind === "closed"
        ? was.get(row.action.action_id)?.action
        : undefined) ?? row.action;
    return marked.sort((a, b) => order(sortAs(a), sortAs(b)));
  }, [prepared, oneAgent, owner, scope.kind, include, sort, showArea, controls, viewKey]);
  const moveCount = rows.filter(
    (row) =>
      !row.action.closed &&
      !row.gone &&
      !!row.source.data.date_moves?.[row.action.action_id],
  ).length;
  // The chip hides once the last move is answered, so its filter goes with it rather than
  // leaving an empty table nothing on screen explains (Noaa, 28 Oct 2026). Worked out, not
  // stored: a past briefing, which has no moves, leaves today's setting alone.
  const movesShown = movesOnly && moveCount > 0;

  // Run names come from one project's runs list; a table across projects names none.
  const single = sources.length === 1 ? sources[0].projectId : "";
  const runNames = useRunNames(
    single,
    !!single && rows.some((row) => !!row.action.closed?.run_id),
  );

  if (rows.length === 0) return <p className={styles.muted}>{empty}</p>;

  const filterKeys = FILTERS_BY_SCOPE[scope.kind];
  // A row closed since the page opened stays, greyed, until the next reload.
  const pool =
    person && !showClosed
      ? rows.filter((row) => !row.action.closed || row.change?.kind === "closed")
      : rows;
  const options = (key: keyof Filters): string[] => {
    const values = [...new Set(pool.map(filterValue[key]))];
    return key === "status"
      ? values.sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
      : values.sort();
  };
  // Counted over what the list can show, so "Show only these" shows every row counted.
  const changes = pool.flatMap((row) => (row.change ? [row.change] : []));
  const needle = query.trim().toLowerCase();
  const onSide = (row: TableRow, side: Side): boolean => {
    const on = onWhom(row.action).side;
    return side === "me"
      ? on === "us" && !!viewer && row.action.internal_owner.toLowerCase() === viewer
      : on === side;
  };
  const shown = pool.filter(
    (row) =>
      (!needle || searchText(row).includes(needle)) &&
      // A closed action is on nobody, so On me / On them never hide one the Closed chip
      // asked for, nor one closed or gone since the page opened.
      (sides.length === 0 ||
        (showClosed && !!row.action.closed) ||
        row.change?.kind === "closed" ||
        row.gone ||
        sides.some((side) => onSide(row, side))) &&
      (!blockingOnly || !!row.action.blocking) &&
      (!movesShown || !!row.source.data.date_moves?.[row.action.action_id]) &&
      (!changedOnly || !!row.change) &&
      // As the dashboard counts them: open, and coloured red or yellow.
      (dueShown.length === 0 ||
        (!row.action.closed && dueShown.some((show) => row.tone === SHOW_TONE[show]))) &&
      filterKeys.every(
        (key) =>
          filters[key].length === 0 ||
          filters[key].includes(filterValue[key](row)),
      ),
  );
  const filtered =
    !!needle ||
    sides.length > 0 ||
    blockingOnly ||
    movesShown ||
    changedOnly ||
    dueShown.length > 0 ||
    Object.values(filters).some((values) => values.length > 0);
  const toggleSide = (side: Side) =>
    updateView(({ sides: current }) => ({
      sides: current.includes(side)
        ? current.filter((s) => s !== side)
        : [...current, side],
    }));
  const onUsLabel = person
    ? person.viewer
      ? "On me"
      : `On ${person.name}`
    : "On us";
  // One person's list is already theirs; elsewhere "On me" is the viewer's share of "On us".
  const sideChips: readonly Side[] = person ? ["us", "them"] : ["me", "us", "them"];
  const sideLabel: Record<Side, string> = { me: "On me", us: onUsLabel, them: "On them" };
  // The full lists take bulk actions: a tick box on each open row.
  const selectable = controls;
  const pickable = shown
    .filter((r) => !r.action.closed && !r.gone)
    .map((r) => r.action.action_id);
  const pickedShown = pickable.filter((id) => picked.has(id));
  const setPick = (ids: string[], on: boolean) =>
    setPicked((was) => {
      const next = new Set(was);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const columns = 5 + (showAgent ? 1 : 0) + (showProject ? 1 : 0) + (selectable ? 1 : 0);
  const pickedRows: BulkRow[] = shown
    .filter((r) => picked.has(r.action.action_id) && !r.action.closed && !r.gone)
    .map((r) => ({
      action: r.action,
      agent: r.agent,
      agentLabel: r.agentLabel,
      projectId: r.source.projectId,
      projectName: r.source.projectName,
      version: r.version,
      today: r.source.data.lists_today,
      dateMove: r.source.data.date_moves?.[r.action.action_id] ?? null,
    }));
  return (
    <div className={styles.wrap}>
      {controls && (
        <div className={styles.filters} role="group" aria-label="Show">
          {scope.kind !== "agent" && (
            <input
              type="search"
              className={styles.search}
              placeholder="Search actions"
              aria-label="Search actions"
              value={query}
              onChange={(e) => updateView(() => ({ query: e.target.value }))}
            />
          )}
          {/* Two groups of chips, whose move then which state, a divider between them
              so they read as two; the dropdowns after them look different already. */}
          <span className={styles.chipGroup}>
            <span className={styles.show}>Show</span>
            {sideChips.map((side) => (
              <button
                key={side}
                type="button"
                className={`${styles.sideChip} ${sides.includes(side) ? styles.sideOn : ""}`}
                aria-pressed={sides.includes(side)}
                onClick={() => toggleSide(side)}
              >
                {sideLabel[side]}
              </button>
            ))}
          </span>
          <span className={styles.divider} aria-hidden />
          <span className={styles.chipGroup}>
            <button
              type="button"
              className={`${styles.sideChip} ${blockingOnly ? styles.sideOn : ""}`}
              aria-pressed={blockingOnly}
              onClick={() => updateView((was) => ({ blockingOnly: !was.blockingOnly }))}
            >
              Blocking
            </button>
            {moveCount > 0 && (
              <button
                type="button"
                className={`${styles.sideChip} ${movesShown ? styles.sideOn : ""}`}
                aria-pressed={movesShown}
                onClick={() => updateView((was) => ({ movesOnly: !was.movesOnly }))}
              >
                Date moves to approve ({moveCount})
              </button>
            )}
            {([SHOW_OVERDUE, SHOW_UPCOMING] as const).map((show) => (
              <button
                key={show}
                type="button"
                className={`${styles.sideChip} ${dueShown.includes(show) ? styles.sideOn : ""}`}
                aria-pressed={dueShown.includes(show)}
                onClick={() => toggleDue(show)}
              >
                {show === SHOW_OVERDUE ? "Overdue" : "Upcoming"}
              </button>
            ))}
            {person && (
              <button
                type="button"
                className={`${styles.sideChip} ${showClosed ? styles.sideOn : ""}`}
                aria-pressed={showClosed}
                onClick={() => updateView((was) => ({ showClosed: !was.showClosed }))}
              >
                Closed
              </button>
            )}
          </span>
          <span className={`${styles.chipGroup} ${styles.selectGroup}`}>
            {filterKeys.map((key) => (
              <MultiSelectFilter
                key={key}
                noun={FILTER_NOUN[key]}
                size="compact"
                searchable={key === "on" || key === "project"}
                options={options(key)}
                selected={filters[key]}
                onChange={(next) =>
                  updateView((was) => ({ filters: { ...was.filters, [key]: next } }))
                }
              />
            ))}
          </span>
          {filtered && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                updateView(() => ({
                  filters: NO_FILTERS,
                  query: "",
                  sides: [],
                  blockingOnly: false,
                  movesOnly: false,
                }));
                setChangedOnly(false);
                setDueShown([]);
              }}
            >
              Clear filters
            </button>
          )}
          <span className={styles.count}>
            Showing {shown.length} of {pool.length} action
            {pool.length === 1 ? "" : "s"}
            {/* In this line rather than one of its own, so a change landing never moves
                the table (Noaa, 29 Oct 2026). */}
            {changes.length > 0 && (
              <>
                {" · "}
                <button
                  type="button"
                  className={styles.countLink}
                  aria-pressed={changedOnly}
                  title={`Since you opened this page: ${changeTally(changes)}`}
                  onClick={() => setChangedOnly((was) => !was)}
                >
                  {changedOnly ? "show all" : `${changes.length} changed`}
                </button>
              </>
            )}
          </span>
          <span className={styles.divider} aria-hidden />
          <label className={styles.control}>
            Sort
            <select
              value={sort}
              onChange={(e) => updateView(() => ({ sort: e.target.value as ActionSort }))}
            >
              <option value="priority">Priority</option>
              <option value="due">Due date</option>
              <option value="deadline">Deadline</option>
            </select>
          </label>
        </div>
      )}
      {!controls && dueShown.length > 0 && (
        <p className={styles.dueOnly}>
          Showing {dueShown.join(" and ")} actions only.{" "}
          <button
            type="button"
            className={styles.clear}
            onClick={() => setDueShown([])}
          >
            Show all
          </button>
        </p>
      )}
      {selectable && (
        <BulkActions
          rows={pickedRows}
          showProject={showProject}
          projectName={(id) => sources.find((x) => x.projectId === id)?.projectName ?? ""}
          onDone={(stillTicked) => setPicked(new Set(stillTicked))}
          onClear={() => setPicked(new Set())}
        />
      )}
      <div className={styles.scroll}>
        <table className={styles.table}>
          <colgroup>
            {selectable && <col className={styles.pickCol} />}
            <col className={styles.rankCol} />
            <col className={styles.titleCol} />
            {showProject && <col />}
            {showAgent && <col />}
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th className={styles.pickCell}>
                  <input
                    type="checkbox"
                    aria-label="Select every open action shown"
                    checked={pickable.length > 0 && pickedShown.length === pickable.length}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          pickedShown.length > 0 && pickedShown.length < pickable.length;
                    }}
                    disabled={pickable.length === 0}
                    onChange={(e) => setPick(pickable, e.target.checked)}
                  />
                </th>
              )}
              <th
                className={styles.rankCell}
                title={
                  scope.kind === "agent"
                    ? "Priority: this specialist's own order"
                    : scope.kind === "all"
                      ? "Priority: the Manager's order across the project"
                      : "Priority: the Manager's order within each project"
                }
              >
                #
              </th>
              <th className={styles.titleHead}>Title</th>
              {showProject && <th>Project</th>}
              {showAgent && <th>Agent</th>}
              <th>Due</th>
              <th>On</th>
              <th>Status</th>
            </tr>
          </thead>
          <RunNamesProvider value={runNames}>
            {shown.map((row) => (
              <ActionRow
                key={`${row.action.action_id}${row.gone ? ":gone" : ""}`}
                row={row}
                showAgent={showAgent}
                showProject={showProject}
                columns={columns}
                open={openIds.has(row.action.action_id)}
                onToggle={() => toggle(row.action.action_id)}
                onClose={() => close(row.action.action_id)}
                anchored={anchored}
                pick={
                  selectable && !row.gone
                    ? {
                        checked: picked.has(row.action.action_id),
                        onChange: (on) => setPick([row.action.action_id], on),
                      }
                    : null
                }
              />
            ))}
          </RunNamesProvider>
          {shown.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={columns} className={styles.muted}>
                  {!controls && dueShown.length > 0
                    ? `Nothing ${dueShown.join(" or ")} here.`
                    : "No actions match these filters."}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};
